/** Mise en forme partagée de la calculatrice Devis Green. */
export const eur = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }) : '—');
export const hoursLabel = (h) => {
  const minutes = Math.round((Number(h) || 0) * 60);
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return mm ? `${hh}h${String(mm).padStart(2, '0')}` : `${hh}h`;
};
export const num = (v, fallback = 0) => {
  const n = parseFloat(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : fallback;
};

export const STATUS_STYLE = {
  0: 'border-border text-muted-foreground bg-secondary/60',
  1: 'border-[hsl(var(--brand-sky))] text-foreground bg-[hsl(var(--brand-sky))]/40',
  2: 'border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10',
  3: 'border-destructive/40 text-destructive bg-destructive/10',
  4: 'border-[hsl(var(--brand-sage))] text-foreground bg-[hsl(var(--brand-mint))]',
};

let seq = 0;
export const localId = () => `l${Date.now().toString(36)}${(seq += 1)}`;

export const newHaie = (name = '') => ({ key: localId(), name, length: '', width: '', height: '', reduction: '', faces: 'devant_dessus', evacuate: true });
export const newTonte = (name = '') => ({ key: localId(), name, surface: '', grass_height: '10', target_height: '4', terrain: 'droit', clutter: 1, technical: 1, waste: 'pickup' });
export const newCustomLine = () => ({ key: localId(), mode: 'free', label: '', qty: '1', unit_price_ttc: '', product_id: '', tva_tx: '20', sap_eligible: false });

export const EMPTY_INPUTS = {
  rhythm: 'ponctuel',
  visits: 1,
  scope: 'passage',
  haies: [],
  tontes: [],
  extras: {},
  custom_lines: [],
  adjustments: {},
  discount_percent: '',
  km: '',
  comment: '',
};

/** Formulaire (chaînes) → charge utile numérique attendue par l'API. */
export const toPayload = (form) => ({
  rhythm: form.rhythm,
  visits: Math.max(1, parseInt(form.visits, 10) || 1),
  scope: form.scope,
  haies: (form.haies || []).map((h) => ({
    enabled: true, name: h.name || null,
    length: num(h.length), width: num(h.width), height: num(h.height), reduction: num(h.reduction), faces: h.faces, evacuate: !!h.evacuate,
  })),
  tontes: (form.tontes || []).map((t) => ({
    enabled: true, name: t.name || null,
    surface: num(t.surface), grass_height: num(t.grass_height, 10), target_height: num(t.target_height, 4),
    terrain: t.terrain, clutter: Math.round(num(t.clutter, 1)), technical: Math.round(num(t.technical, 1)), waste: t.waste,
  })),
  extras: Object.fromEntries(Object.entries(form.extras || {}).filter(([, v]) => num(v.qty) > 0).map(([k, v]) => [k, { qty: num(v.qty), modifiers: v.modifiers || [] }])),
  custom_lines: (form.custom_lines || [])
    .filter((l) => num(l.qty) > 0 && ((l.mode === 'service' && l.product_id) || (l.mode === 'free' && l.label.trim() && l.unit_price_ttc !== '')))
    .map((l) => (l.mode === 'service'
      ? { label: l.label || '', qty: num(l.qty, 1), product_id: Number(l.product_id), sap_eligible: !!l.sap_eligible }
      : { label: l.label.trim(), qty: num(l.qty, 1), unit_price_ttc: num(l.unit_price_ttc), tva_tx: num(l.tva_tx, 20), sap_eligible: !!l.sap_eligible })),
  adjustments: Object.fromEntries(Object.entries(form.adjustments || {}).filter(([, v]) => Number(v))),
  discount_percent: Math.min(100, Math.max(0, num(form.discount_percent))),
  km: num(form.km),
  comment: form.comment || null,
});

/** Charge utile API (devis / brouillon enregistré) → formulaire, pour dupliquer ou reprendre. */
export const fromPayload = (p) => {
  if (!p) return EMPTY_INPUTS;
  const str = (v) => (v === null || v === undefined || v === 0 ? '' : String(v));
  const haies = [...(p.haies || []), ...(p.haie && p.haie.enabled !== false && p.haie.length ? [p.haie] : [])];
  const tontes = [...(p.tontes || []), ...(p.tonte && p.tonte.enabled !== false && p.tonte.surface ? [p.tonte] : [])];
  return {
    rhythm: p.rhythm || 'ponctuel',
    visits: p.visits || 1,
    scope: p.scope || 'passage',
    haies: haies.map((h) => ({ ...newHaie(h.name || ''), length: str(h.length), width: str(h.width), height: str(h.height), reduction: str(h.reduction), faces: h.faces || 'devant_dessus', evacuate: h.evacuate !== false })),
    tontes: tontes.map((t) => ({ ...newTonte(t.name || ''), surface: str(t.surface), grass_height: String(t.grass_height ?? 10), target_height: String(t.target_height ?? 4), terrain: t.terrain || 'droit', clutter: t.clutter || 1, technical: t.technical || 1, waste: t.waste || 'pickup' })),
    extras: Object.fromEntries(Object.entries(p.extras || {}).map(([k, v]) => [k, { qty: str(v.qty), modifiers: v.modifiers || [] }])),
    custom_lines: (p.custom_lines || []).map((l) => ({ ...newCustomLine(), mode: l.product_id ? 'service' : 'free', label: l.label || '', qty: String(l.qty ?? 1), unit_price_ttc: str(l.unit_price_ttc), product_id: l.product_id ? String(l.product_id) : '', tva_tx: String(l.tva_tx ?? 20), sap_eligible: l.sap_eligible === true })),
    adjustments: { ...(p.adjustments || {}) },
    discount_percent: str(p.discount_percent),
    km: str(p.km),
    comment: p.comment || '',
  };
};

export const hasContent = (p) => p.haies.some((h) => h.length > 0 && h.height > 0) || p.tontes.some((t) => t.surface > 0) || Object.keys(p.extras).length > 0 || p.custom_lines.length > 0 || p.km > 0;
